import api from './api';

export interface Partner {
  id: number;
  name: string;
  projects?: ProjectPartner[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ProjectPartner {
  id: number;
  projectId: number;
  partnerId: number;
  percentage: number;
  project: {
    id: number;
    name: string;
  };
  partner?: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreatePartnerData {
  name: string;
}

export interface AssignPartnerData {
  partnerId: number;
  projectId: number;
  percentage: number;
}

export interface PaginatedPartnersResponse {
  partners: Partner[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class PartnerService {
  async getPartners(params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<{
    partners: Partner[];
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.search) searchParams.append('search', params.search);

    const response = await api.get(`/partners?${searchParams.toString()}`);
    return {
      partners: response.data.partners,
      total: response.data.pagination.total,
      totalPages: response.data.pagination.pages,
      page: response.data.pagination.page,
      limit: response.data.pagination.limit
    };
  }

  async createPartner(data: CreatePartnerData): Promise<Partner> {
    const response = await api.post('/partners', data);
    return response.data;
  }

  async getPartner(id: number): Promise<Partner> {
    const response = await api.get(`/partners/${id}`);
    return response.data;
  }

  async updatePartner(id: number, data: CreatePartnerData): Promise<Partner> {
    const response = await api.put(`/partners/${id}`, data);
    return response.data;
  }

  async deletePartner(id: number): Promise<void> {
    await api.delete(`/partners/${id}`);
  }

  async assignPartnerToProject(data: AssignPartnerData): Promise<ProjectPartner> {
    const response = await api.post('/partners/assign', data);
    return response.data;
  }

  async updatePartnerAssignment(id: number, percentage: number): Promise<ProjectPartner> {
    const response = await api.put(`/partners/assignment/${id}`, { percentage });
    return response.data;
  }

  async removePartnerFromProject(id: number): Promise<void> {
    await api.delete(`/partners/assignment/${id}`);
  }

  async getProjectPartners(projectId: number): Promise<ProjectPartner[]> {
    const response = await api.get(`/partners/project/${projectId}`);
    return response.data;
  }
}

export const partnerService = new PartnerService();
